import { useEffect, useRef, useCallback } from 'react';

/**
 * Auto-save draft + beforeunload warning + save on unmount
 */
export const usePageProtection = ({
  gridData,
  zooplanktonGridData,
  sampleDate,
  selectedArea,
  customOrganisms,
  colorOverrides,
  depthOverrides,
  temperatureData,
  saveDraft,
  isPastingRef
}) => {
  const saveDraftRef = useRef(saveDraft);
  const renderRef = useRef(0);
  const pendingSaveRef = useRef(false); // Track if there are unsaved changes
  const lastSavedDataRef = useRef(null); // Track last saved snapshot
  
  useEffect(() => { saveDraftRef.current = saveDraft; }, [saveDraft]);

  // Immediate save function (no debounce)
  const saveNow = useCallback(async () => {
    if (isPastingRef?.current) return;
    try {
      pendingSaveRef.current = false;
      await saveDraftRef.current();
    } catch (e) { /* silent */ }
  }, [isPastingRef]);
  
  // Warn before leaving AND save
  useEffect(() => {
    const handler = (e) => {
      const hasData = Object.values(gridData).some(d => 
        Object.values(d).some(v => v && v.trim())
      ) || Object.values(zooplanktonGridData).some(d => 
        Object.values(d).some(v => v && v.trim())
      );
      if (hasData) {
        e.preventDefault();
        e.returnValue = '';
        // Fire-and-forget save on beforeunload
        if (pendingSaveRef.current) {
          try { saveDraftRef.current(); } catch (_) {}
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [gridData, zooplanktonGridData]);

  // Save when tab becomes hidden (user switches tabs)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden' && pendingSaveRef.current) {
        try { saveDraftRef.current(); } catch (_) {}
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);
  
  // Auto-save: 1s debounce, skip only first render
  useEffect(() => {
    renderRef.current += 1;
    if (renderRef.current === 1) return;
    if (isPastingRef?.current) return;
    
    pendingSaveRef.current = true; // Mark as having unsaved changes
    
    const id = setTimeout(async () => {
      if (isPastingRef?.current) return;
      try {
        pendingSaveRef.current = false;
        await saveDraftRef.current();
      } catch (e) { /* silent */ }
    }, 1000);
    
    return () => clearTimeout(id);
  }, [gridData, sampleDate, selectedArea, customOrganisms, colorOverrides, zooplanktonGridData, depthOverrides, temperatureData, isPastingRef]);

  // CRITICAL: Save on component unmount (SPA navigation)
  useEffect(() => {
    return () => {
      if (pendingSaveRef.current) {
        try { saveDraftRef.current(); } catch (_) {}
      }
    };
  }, []);

  return { saveNow };
};
